import type { dashboard } from '@cwos/api-client';
import type { Severity } from '@cwos/ui-shared';

/**
 * Pure, DOM-free derivations for the analytics workspace (Track B). Mirrors
 * `dashboardLogic.ts`: the backend owns every number; these helpers only decide
 * how a value is presented. The preset/view labels, the change chip, and the
 * numeric formatters are unit tested independently of rendering.
 */

export type AnalyticsPreset = dashboard.AnalyticsPreset;

export type AnalyticsView =
  'pulse' | 'staff' | 'schedule' | 'clients' | 'careGaps' | 'documentation' | 'impact' | 'monitor';

export const ANALYTICS_VIEWS: { value: AnalyticsView; label: string }[] = [
  { value: 'pulse', label: 'Pulse' },
  { value: 'staff', label: 'Staff' },
  { value: 'schedule', label: 'Schedule' },
  { value: 'clients', label: 'Clients' },
  // OPERATIONAL LANGUAGE (owner ruling, verified against the data): the
  // measure behind this view is service-delivery lag against per-client
  // TARGETS (`ClientCareGap`, 7/30-day lookbacks) — an operational gap, not a
  // clinical care-plan concept. Phibian is not an EHR, and the label now says
  // what the number actually is.
  { value: 'careGaps', label: 'Service gaps' },
  { value: 'documentation', label: 'Documentation' },
  { value: 'impact', label: 'Impact' },
  { value: 'monitor', label: 'Monitor' },
];

/**
 * THE FIVE LENSES (owner directive, Phase G): the eight views regroup under
 * five analytical lenses — Overview · Workforce · Operations · Compliance ·
 * Performance — without losing a single view, dataset, filter or deep link.
 * The `view` URL param stays the eight-value vocabulary, so every legacy
 * `?view=` link resolves exactly as before; the lens is derived from the view
 * and never stored.
 */
export const ANALYTICS_LENSES: {
  key: string;
  label: string;
  views: AnalyticsView[];
}[] = [
  { key: 'overview', label: 'Overview', views: ['pulse'] },
  { key: 'workforce', label: 'Workforce', views: ['staff'] },
  { key: 'operations', label: 'Operations', views: ['schedule', 'monitor'] },
  { key: 'compliance', label: 'Compliance', views: ['documentation'] },
  { key: 'performance', label: 'Performance', views: ['impact', 'clients', 'careGaps'] },
];

/**
 * The periods this workspace offers, and what each is called on screen.
 *
 * `as const satisfies`, NOT a plain annotation, and the difference is the whole
 * guard (F350). Declared as `: { value: AnalyticsPreset; label: string }[]` the
 * entries are WIDENED to `AnalyticsPreset` on the way in, so
 * `(typeof ANALYTICS_PRESETS)[number]['value']` is `AnalyticsPreset` whatever
 * the array actually contains — and a parity assertion written against it
 * compares that type to itself and passes for any list at all, including an
 * empty one. That vacuity was found by mutation: deleting an entry here left
 * `pnpm typecheck` completely silent.
 *
 * `as const` keeps the literal types so the union means what it says, and
 * `satisfies` keeps the check the annotation was there for — an entry naming a
 * period the client contract does not have is still an error here. Together
 * they make `apps/admin/test/unit/analytics-preset-vocabulary.test.ts` a real
 * compile-time parity check against `dashboard.AnalyticsPreset`.
 */
export const ANALYTICS_PRESETS = [
  { value: 'this_week', label: 'This week' },
  { value: 'last_week', label: 'Last week' },
  { value: 'this_month', label: 'This month' },
  { value: 'last_month', label: 'Last month' },
  { value: 'last_30_days', label: 'Last 30 days' },
  { value: 'ytd', label: 'YTD' },
  { value: 'all', label: 'All' },
  { value: 'custom', label: 'Custom' },
] as const satisfies readonly { value: AnalyticsPreset; label: string }[];

export function normalizePreset(raw: string | undefined): AnalyticsPreset {
  const match = ANALYTICS_PRESETS.find((p) => p.value === raw);
  return match ? match.value : 'this_week';
}

export function normalizeView(raw: string | undefined): AnalyticsView {
  const match = ANALYTICS_VIEWS.find((v) => v.value === raw);
  return match ? match.value : 'pulse';
}

export function presetLabel(preset: AnalyticsPreset): string {
  return ANALYTICS_PRESETS.find((p) => p.value === preset)?.label ?? preset;
}

/**
 * A change chip label like “+12.5%”, “−5%”, or “—”.
 *
 * `changePct` ALREADY carries its own sign — the API sends −4.2 for a fall —
 * and this prepended a second one from `direction`, so every declining metric
 * on the analytics pulse rendered “−-4.2%”. The magnitude is what the sign
 * decorates, so the magnitude is what gets formatted: `direction` decides the
 * glyph, `Math.abs` supplies the number, and the two can no longer disagree.
 *
 * The minus is U+2212, not a hyphen: a hyphen at this size next to a digit
 * reads as a dash between two things.
 */
export function changeLabel(
  changePct: number | null,
  direction: dashboard.HeadlineMetric['direction'],
): string {
  if (changePct == null) return '—';
  const sign = direction === 'up' ? '+' : direction === 'down' ? '−' : '';
  return `${sign}${Math.abs(changePct)}%`;
}

/** Tone for a headline change — for the four volume metrics, “up” is good. */
export function changeTone(direction: dashboard.HeadlineMetric['direction']): Severity {
  return direction === 'up' ? 'good' : direction === 'down' ? 'look' : 'plain';
}

export function formatMetricValue(
  value: number,
  unit: 'hours' | 'count',
  precision: number,
): string {
  const n = formatNumber(value, precision);
  return unit === 'hours' ? `${n}h` : n;
}

export function formatNumber(value: number, precision = 0): string {
  const rounded = Number(value.toFixed(precision));
  return rounded.toLocaleString('en-US', {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  });
}

/** “2026-08-10” → “Aug 10”. */
export function weekShortLabel(iso: string): string {
  const [y, m, d] = iso.split('-').map((part) => Number(part));
  if (!y || !m || !d) return iso;
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  return `${months[(m - 1) % 12] ?? ''} ${d}`;
}

/** True when the preset requires explicit from/to inputs. */
export function isCustomPreset(preset: AnalyticsPreset): boolean {
  return preset === 'custom';
}

/** Calendar day `iso` (YYYY-MM-DD) plus `n` days. Pure date arithmetic, no zone. */
export function addDays(iso: string, n: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y!, m! - 1, d! + n)).toISOString().slice(0, 10);
}

/**
 * The LAST day a period covers. `PeriodLabel.to` is EXCLUSIVE — the server's
 * half-open [from, to): "this week" ends at tomorrow, "last month" at the 1st
 * of this one — so the last day counted is the day before it.
 */
export function lastDay(period: Pick<dashboard.PeriodLabel, 'to'>): string {
  return addDays(period.to, -1);
}

/**
 * The period as a question names it: the preset's own name ("last week"), or
 * — for a custom range, whose name says nothing — the actual dates, so Ask is
 * asked about the window the figure counted.
 */
export function askPeriod(period: dashboard.PeriodLabel): string {
  return period.preset === 'custom' && period.from != null
    ? `${period.from} to ${lastDay(period)}`
    : period.label.toLowerCase();
}

/**
 * "This week (Sep 8 – Sep 14)" — the RESOLVED window, stated on the card that
 * carries the number it was computed over.
 *
 * WHY A CARD NEEDS ITS OWN COPY OF THE PRESET NAME. The page around it already
 * shows `period.label` once, as page-level context — but a card can outlive
 * that context: it is what a reader screenshots, pastes into a message, or
 * compares against a Dashboard tile answering the "same" metric. `period.label`
 * alone ("This week") repeats what the page already said; the dates are what
 * make the card self-describing without it.
 *
 * `from` is `null` only for the unbounded "All" preset (`PeriodLabel.from` is
 * nullable for exactly that reason), and the sentence degrades to the preset's
 * own name rather than a dash pointing at nothing.
 */
export function periodRangeLabel(period: dashboard.PeriodLabel): string {
  if (period.from == null) return period.label;
  return `${period.label} (${weekShortLabel(period.from)} – ${weekShortLabel(lastDay(period))})`;
}
